import { useState } from "react";
import Linkify from "../components/Linkify";
import { addMemo, updateMemo, deleteMemo } from "../api/memos";

const BookMemos = ({ book, currentUser, onMemoAdded }) => {
  /** 기록칸을 늘 펼쳐두면 화면의 절반을 먹는다. 문장 옮기기와 같이
      버튼으로 접어두고, 누를 때만 편다. */
  const [writing, setWriting] = useState(false);
  const [memoText, setMemoText] = useState("");
  const [memoPublic, setMemoPublic] = useState(false);
  const [memoLocation, setMemoLocation] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingMemo, setEditingMemo] = useState(null);
  const [editMemoText, setEditMemoText] = useState("");
  const [editMemoPublic, setEditMemoPublic] = useState(false);

  const uid = currentUser?.uid;
  const memos = (book.memos || []).filter(m => m.userId === uid);
  const sorted = [...memos].sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  const handleAddMemo = async () => {
    if (!memoText.trim() || !uid) return;
    setSaving(true);
    try {
      await addMemo({
        bookId: book.id,
        userId: uid,
        text: memoText,
        isPublic: memoPublic,
        location: memoLocation,
      });
      if (onMemoAdded) onMemoAdded(book.id, null);
      setMemoText("");
      setMemoLocation("");
      setWriting(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMemo = async (memo) => {
    // 댓글과 좋아요는 on delete cascade 로 함께 사라진다
    await deleteMemo(memo.id);
    if (onMemoAdded) onMemoAdded(book.id, null);
  };

  const startEditMemo = (memo) => {
    setEditingMemo(memo.id);
    setEditMemoText(memo.text);
    setEditMemoPublic(memo.isPublic);
  };

  // 예전엔 books 문서를 통째로 읽어 배열을 map 으로 갈아끼웠다.
  // 이제 해당 메모 행만 수정하므로 동시 편집이 서로를 덮어쓰지 않는다.
  const handleEditMemo = async (oldMemo) => {
    if (!editMemoText.trim()) return;
    setSaving(true);
    try {
      await updateMemo(oldMemo.id, { text: editMemoText, isPublic: editMemoPublic });
      setEditingMemo(null);
      setEditMemoText("");
      if (onMemoAdded) onMemoAdded(book.id, null);
    } finally {
      setSaving(false);
    }
  };

  const locMemos = sorted.filter(m => m.location);

  return (
    <div className="memo-wrap">
      {!currentUser?.isAnonymous && !writing && (
        <div className="memo-addwrap">
          <button className="memo-add" onClick={() => setWriting(true)}>+ 기록 남기기</button>
        </div>
      )}
      {!currentUser?.isAnonymous && writing && (
        <div className="memo-compose">
          <textarea
            placeholder="이 책에 대한 기록을 남겨보세요..."
            value={memoText}
            autoFocus
            onChange={e => setMemoText(e.target.value)}
          />
          <div className="memo-location-row">
            <span className="memo-location-icon">📍</span>
            <input
              className="memo-location-input"
              placeholder="읽은 장소 (선택)"
              value={memoLocation}
              onChange={e => setMemoLocation(e.target.value)}
            />
          </div>
          <div className="memo-compose-hint">기록은 기본적으로 나만 봅니다. 체크하면 다른 독자에게도 보입니다.</div>
          <div className="memo-compose-footer">
            <label className="memo-toggle">
              <input type="checkbox" checked={memoPublic} onChange={e => setMemoPublic(e.target.checked)} />
              <span className="memo-toggle-label">이야기 나누기에 공개하기</span>
            </label>
            <div className="memo-edit-actions">
              <button
                className="btn-cancel"
                onClick={() => { setWriting(false); setMemoText(""); setMemoLocation(""); }}
              >취소</button>
              <button className="btn-memo-save" onClick={handleAddMemo} disabled={!memoText.trim() || saving}>
                {saving ? "저장 중..." : "기록하기"}
              </button>
            </div>
          </div>
        </div>
      )}
      {sorted.length === 0 ? (
        <div className="memo-empty">아직 기록이 없어요. 첫 메모를 남겨보세요 ✍️</div>
      ) : (
        sorted.map(m => (
          <div key={m.id} className="memo-item">
            {editingMemo === m.id ? (
              <div className="memo-edit">
                <textarea
                  className="memo-edit-textarea"
                  value={editMemoText}
                  onChange={e => setEditMemoText(e.target.value)}
                  autoFocus
                />
                <div className="memo-compose-footer">
                  <label className="memo-toggle">
                    <input type="checkbox" checked={editMemoPublic} onChange={e => setEditMemoPublic(e.target.checked)} />
                    <span className="memo-toggle-label">이야기 나누기에 공개하기</span>
                  </label>
                  <div className="memo-edit-actions">
                    <button className="btn-cancel" onClick={() => setEditingMemo(null)}>취소</button>
                    <button className="btn-memo-save" onClick={() => handleEditMemo(m)} disabled={!editMemoText.trim() || saving}>
                      {saving ? "저장 중..." : "수정 완료"}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="memo-item-header">
                  <span className="memo-item-date">{m.createdAt}</span>
                  <span className={`memo-item-visibility${m.isPublic ? " public" : ""}`}>{m.isPublic ? "공개" : "비공개"}</span>
                  <button className="memo-item-edit" onClick={() => startEditMemo(m)}>✎</button>
                  <button className="memo-item-delete" onClick={() => handleDeleteMemo(m)}>✕</button>
                </div>
                {m.location && <div className="memo-item-location">📍 {m.location}</div>}
                <div className="memo-item-text"><Linkify text={m.text} /></div>
              </>
            )}
          </div>
        ))
      )}
      {/* 장소 궤적 지도 */}
      {locMemos.length > 0 && (
        <div className="location-trail">
          <div className="location-trail-title">📍 독서 궤적</div>
          <div className="location-trail-map">
            {locMemos.map((m, i) => (
              <div key={m.id} className="location-trail-node" style={{ animationDelay: `${i * 0.15}s` }}>
                <div className="location-trail-dot" />
                {i < locMemos.length - 1 && <div className="location-trail-line" />}
                <div className="location-trail-info">
                  <div className="location-trail-place">{m.location}</div>
                  <div className="location-trail-date">{m.createdAt}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookMemos;
